'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeletePostPurchaseRules } from '@/lib/api/postPurchaseRules'

interface BulkDeletePostPurchaseRulesModalProps {
	ruleIds: string[]
	storeId: string
	onClose: () => void
	onSuccess?: () => void
}

/**
 * BulkDeletePostPurchaseRulesModal component for confirming bulk deletion of rules
 */
export const BulkDeletePostPurchaseRulesModal = ({
	ruleIds,
	storeId,
	onClose,
	onSuccess,
}: BulkDeletePostPurchaseRulesModalProps) => {
	const { t } = useTranslation('postPurchaseRules')
	const deleteRulesMutation = useDeletePostPurchaseRules()

	const handleDelete = async () => {
		await deleteRulesMutation.mutateAsync({ storeId, ids: ruleIds })
		onSuccess?.()
		onClose()
	}

	return (
		<AlertDialog open={!!ruleIds.length} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.bulkDelete.title', { count: ruleIds.length })}</AlertDialogTitle>
					<AlertDialogDescription>
						{t('modals.bulkDelete.description')}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel onClick={onClose}>{t('actions.cancel')}</AlertDialogCancel>
					<AlertDialogAction
						onClick={handleDelete}
						disabled={deleteRulesMutation.isPending}
						className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
					>
						{deleteRulesMutation.isPending ? t('modals.delete.deleting') : t('modals.delete.delete')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

